import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Activity, UserPlus, CheckCircle, Trophy, MessageCircle, Flame } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface ChallengeActivityProps {
  challengeId: string;
}

const ACTIVITY_ICONS = {
  joined: UserPlus,
  completed_day: CheckCircle,
  milestone: Trophy,
  streak: Flame,
  comment: MessageCircle,
  reaction: Flame,
};

const ACTIVITY_COLORS = {
  joined: "text-accent",
  completed_day: "text-secondary",
  milestone: "text-primary",
  streak: "text-accent",
  comment: "text-primary",
  reaction: "text-secondary",
};

export function ChallengeActivity({ challengeId }: ChallengeActivityProps) {
  const { data: activities, isLoading } = useQuery({
    queryKey: ['challenge-activity', challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('challenge_activity')
        .select('*')
        .eq('challenge_id', challengeId)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data;
    },
  });

  const getActivityText = (activity: any) => {
    switch (activity.activity_type) {
      case 'joined':
        return 'joined the challenge';
      case 'completed_day':
        return 'completed a day';
      case 'milestone':
        return `reached ${activity.activity_data?.milestone} milestone`;
      case 'streak':
        return `is on a ${activity.activity_data?.streak_days} day streak!`;
      case 'comment':
        return `commented: "${activity.activity_data?.preview}..."`;
      case 'reaction':
        return `sent a ${activity.activity_data?.reaction_type} reaction`;
      default:
        return 'updated their progress';
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="h-5 w-5" />
        <h3 className="text-lg font-semibold">Activity Feed</h3>
      </div>

      <ScrollArea className="h-[400px]">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading activity...</p>
        ) : activities && activities.length > 0 ? (
          <div className="space-y-3">
            {activities.map((activity) => {
              const Icon = ACTIVITY_ICONS[activity.activity_type as keyof typeof ACTIVITY_ICONS] || Activity;
              const color = ACTIVITY_COLORS[activity.activity_type as keyof typeof ACTIVITY_COLORS] || "text-muted-foreground";

              return (
                <div key={activity.id} className="flex gap-3 items-start">
                  <div className={`p-2 rounded-lg bg-muted ${color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">
                      <span className="font-medium">User</span> {getActivityText(activity)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No activity yet. Be the first to log progress!
          </p>
        )}
      </ScrollArea>
    </Card>
  );
}
